// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2015
//
// ## SolfegePanel
//
// An object containing all methods called & properties for
// the letterPanel when using solfege buttons
//
import * as Mousetrap from 'mousetrap';
import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import { solfegeSystems } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import utils from '../utils';
import type LetterPanel from './letterPanel';

type Quality = 'Major' | 'minor' | 'natural' | 'harmonic';

interface Ratio {
  buttonsPerRow: number;
  rows: number;
  ratio: number;
}

const clearSolfegeHotKeys = () => {
  const keys = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    'shift+6',
    '7',
    'shift+7',
    '8',
    'd',
    'r',
    'm',
    'f',
    's',
    'l',
    't',
    'D',
    'R',
    'M',
    'F',
    'S',
    'L',
    'T',
  ];
  keys.forEach((key) => {
    Mousetrap.unbind(key);
  });
};

const findRatios = (n: number, buttonRatio: number) => {
  // console.log('findRatios', n);
  // where n is number of buttons

  const perspectives = {
    widescreen: 4,
    landscape: 2,
    square: 1,
    portrait: 0.5,
    tall: 0.25,
  };

  const ratios: Ratio[] = [];
  for (let i = 1; i <= n; i++) {
    const buttonsPerRow = Math.ceil(n / i);
    const rows = i;
    const ratio = Math.ceil((buttonsPerRow * buttonRatio) / i);

    ratios.push({
      buttonsPerRow,
      rows,
      ratio,
    });
  }

  const findClosest = (ratio: number) =>
    ratios.reduce((prev, curr) =>
      Math.abs(curr.ratio - ratio) < Math.abs(prev.ratio - ratio) ? curr : prev
    );

  // set each perspective to the perspective object from above
  Object.keys(perspectives).forEach((perspective) => {
    perspectives[perspective] = findClosest(perspectives[perspective]);
  });

  return perspectives;
};

export interface SolfegePanelOptions {
  system: SolfegeSystemName;
  quality: Quality;
}

export default class SolfegePanel {
  panel: LetterPanel;

  span: HTMLSpanElement;

  solfegeButtons: {
    Major: {
      value: string;
      text: string;
      doNotAppend: boolean;
      button: HTMLButtonElement;
      scaleDegree: string | number;
    }[];
    minor: {
      value: string;
      text: string;
      doNotAppend: boolean;
      button: HTMLButtonElement;
      scaleDegree: string | number;
    }[];
    system: any;
    parentSpan: HTMLSpanElement;
  };

  // Public constructor
  constructor(panel: LetterPanel, options: SolfegePanelOptions) {
    const { system, quality } = options;

    this.init(panel, system, quality);
  }

  init(panel: LetterPanel, system: SolfegeSystemName, quality: Quality) {
    this.panel = panel;

    this.span = document.createElement('span');
    this.panel.lettersDiv.appendChild(this.span);

    if (system) {
      this.initializeSolfegeButtons(system);
    }
    if (quality) {
      this.setSolfegeButtons(quality);
    }
  }

  initializeSolfegeButtons(system: SolfegeSystemName) {
    // quality: Major or minor
    // system: scaleDegrees, solfegeDoMinor, solfegeLaMinor
    // keysArray: 1 - 8, M6, M7

    // Hotkeys are handled seperately -- since depending on
    // the quality, we want them to behave differently.

    let systemObj: { [degree: string]: number | string } = solfegeSystems.Major[system];

    const buttonMapper = (scaleDegree: string | number) => {
      // gets systemObj by closure
      const solfege = systemObj[scaleDegree];
      const value = `${scaleDegree}`;

      const buttonOptions = {
        value,
        text: `${solfege}`,
        doNotAppend: true,
      };

      const obj = {
        button: this.panel.createCustomButton(buttonOptions) as HTMLButtonElement,
        scaleDegree,
        ...buttonOptions,
      };
      return obj;
    };

    const majorButtons = solfegeSystems.Major.values.map(buttonMapper);

    systemObj = solfegeSystems.minor[system];
    const minorButtons = solfegeSystems.minor.values.map(buttonMapper);

    this.solfegeButtons = {
      Major: majorButtons,
      minor: minorButtons,
      system,
      parentSpan: this.span,
    };
  }

  setSolfegeButtons(quality: Quality, buttonList?: (string | number)[]) {
    // quality may be Major, minor, natural or harmonic.
    // minor alone shows all variants of 6 & 7.
    // natural & harmonic filter to only their variants.
    // Remove all solfege buttons:
    const div = this.span;
    while (div.firstChild) {
      div.removeChild(div.firstChild);
    }

    // Default buttons array from quality:
    let excludeButtonsList: string[] = [];
    let majorOrMinor: 'Major' | 'minor' = 'minor';
    switch (quality) {
      case 'Major':
        majorOrMinor = 'Major';
        break;
      case 'harmonic':
        excludeButtonsList = ['M6', '7'];
        break;
      case 'natural':
        excludeButtonsList = ['M6', 'M7'];
    }

    // Add needed buttons
    // console.log('this.solfegeButtons', this.solfegeButtons);
    // console.log('buttonList', buttonList);
    // Filter the list of buttons:
    const activeButtons = this.solfegeButtons[majorOrMinor].filter((button) => {
      const excluded = excludeButtonsList.indexOf(button.value) !== -1;
      const included = !buttonList || buttonList.indexOf(button.value) !== -1;

      if (included && !excluded) {
        return true;
      }
      return false;
    });

    // Add those buttons to the div, with perspective line breaks:
    const buttonRatio = this.solfegeButtons.system === 'scaleDegrees' ? 1 : 1.5;
    const perspectives = findRatios(activeButtons.length, buttonRatio);
    activeButtons.forEach((button, index) => {
      div.appendChild(button.button);
      this.insertPerspectiveBreak(index, perspectives, div);
    });

    // Add hotkeys:
    this.setSolfegeHotKeys(activeButtons, quality);
  }

  insertPerspectiveBreak(n, perspectivesObj, div) {
    const breaks = [];
    utils.forEach(perspectivesObj, (obj, name) => {
      if ((n + 1) % obj.buttonsPerRow === 0) {
        breaks.push(name);
      }
    });

    if (breaks.length) {
      const buttonObj = {
        tagName: 'br',
        perspective: breaks,
        doNotAppend: true,
      };

      const el = this.panel.createCustomButton.call(this.panel, buttonObj);
      div.appendChild(el);
    }
  }

  private isHidden: boolean = false;

  show() {
    this.span.style.display = 'inline-block';
  }

  hide() {
    this.span.style.display = 'none';
  }

  setSolfegeHotKeys(activeButtons, quality) {
    // first clear them out!
    clearSolfegeHotKeys();
    const majorOrMinor = quality.indexOf('M') !== -1 ? 'Major' : 'minor';
    const systemName = this.solfegeButtons.system;
    const system = solfegeSystems[majorOrMinor][systemName];
    const includeSolfegeHotKeys = systemName.indexOf('solfege') !== -1;

    // for each active button
    activeButtons.forEach((obj) => {
      const degree = obj.scaleDegree;

      // set the scale degree hot key, if numeric:
      let hotKey = utils.isNumeric(degree) ? [`${degree}`] : [];

      // if it's major, set both Upper and lower case hotkey:
      if (quality === 'Major' && includeSolfegeHotKeys) {
        hotKey.push(system[degree][0]);
        hotKey.push(system[degree][0].toLowerCase());
      } else {
        // otherwise, watch out for 6 vs m6, etc...
        switch (degree) {
          case 6:
          case 7:
            hotKey = [`${degree}`];
            if (includeSolfegeHotKeys) {
              hotKey.push(system[degree][0].toLowerCase());
            }
            break;
          case 'M6':
          case 'M7':
            hotKey = [`shift+${degree[1]}`];
            if (includeSolfegeHotKeys) {
              hotKey.push(system[degree][0]);
            }
            break;
          default:
            hotKey = [`${degree}`];
            if (includeSolfegeHotKeys) {
              hotKey.push(system[degree][0]);
              hotKey.push(system[degree][0].toLowerCase());
            }
        }
      }

      // Now check and see if we should do harmonic or natural
      // minor hotKey handlers:
      const six = Number(degree) === 6;
      const seven = Number(degree) === 7 || degree === 'M7';
      /* eslint-enable eqeqeq */
      const harmonic = quality.indexOf('harmonic') !== -1;
      const natural = quality.indexOf('natural') !== -1;

      if ((harmonic || natural) && six) {
        hotKey = ['6'];
        if (includeSolfegeHotKeys) {
          hotKey = hotKey.concat([system.m6[0].toLowerCase(), system.m6[0]]);
        }
      }
      if (harmonic && seven) {
        hotKey = ['7', 'shift+7'];
        if (includeSolfegeHotKeys) {
          hotKey.push(system.M7[0]);
          hotKey.push(system.M7[0].toLowerCase());
        }
      }
      if (natural && seven) {
        hotKey = ['7'];
        if (includeSolfegeHotKeys) {
          hotKey.push(system.m7[0]);
          hotKey.push(system.m7[0].toLowerCase());
        }
      }
      Mousetrap.bind(hotKey, (e) => {
        if (this.isHidden) return;
        e.preventDefault();
        this.panel.customButtonEvent(obj.value, true);
      });
    });
  }
}
